import _ from "lodash"
import clsx from "clsx"
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  CardMedia,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"

import { useVenue } from "providers/venue"
import {
  getFeatureDetail,
  getFeatureIDByPath,
  IAmenityDetail,
} from "utils/feature"
import { ILocation, IServiceConfig } from "providers/venue/types"
import { useMemo } from "react"

export interface IServiceCardProps extends Omit<AccordionProps, "children"> {
  data: IServiceConfig
  sx?: SxProps
  onClickLocation: (location: ILocation) => void
}

export const ServiceCard: React.FC<IServiceCardProps> = ({
  data,
  sx = {},
  onClickLocation,
  ...props
}) => {
  const { t } = useTranslation()
  const { findFeatureById } = useVenue()
  const { locations = [], name, description, feature_image } = data
  const hasLocations = _.size(locations) > 0
  const theme = useTheme()

  const isRtl = theme.isRtl

  const ExpandMoreIcon = useMemo(
    () => getIcon(theme?.assets?.expandIcon) || <ExpandMoreRoundedIcon />,
    [theme]
  )

  const ChevronRightIcon = useMemo(
    () => getIcon(theme?.assets?.nextIcon) || <ChevronRightRoundedIcon />,
    [theme]
  )

  const {
    root,
    accordionSummary,
    summaryContainer,
    titleText,
    descriptionText,
    infoContainer,
    accordionDetails,
    locationList,
    locationListItem,
    locationNavigateButton,
    floorTextAvatar,
    venueLogo: venueLogoStyle,
    locationLogo: locationLogoStyle,
    venueLogoWrapper,
    locationLogoWrapper,
    divider,
    listNameText,
  } = theme.components[`ServiceCard`]?.styleOverrides || {}

  return (
    <Accordion sx={deepmerge(root, sx)} {...props}>
      <AccordionSummary
        expandIcon={hasLocations && ExpandMoreIcon}
        sx={{ ...accordionSummary, backgroundImage: `url(${feature_image})` }}
      >
        <Box sx={summaryContainer}>
          <Box sx={infoContainer}>
            <Typography variant="h4" sx={titleText}>
              {t(name)}
            </Typography>
            <Typography variant="body1" sx={descriptionText}>
              {t(description)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      {hasLocations && (
        <AccordionDetails sx={accordionDetails}>
          <List disablePadding sx={locationList}>
            {locations.map((location, index) => {
              const path = location?.url
              const featureId = getFeatureIDByPath(location?.url)
              const isReferenceID = location.type === "reference-id"
              const feature =
                isReferenceID && featureId ? findFeatureById(featureId) : null
              const {
                venueLogo = "",
                level = "",
                name: locationName = "",
              } = feature
                ? (getFeatureDetail(feature) as IAmenityDetail) || {}
                : {}

              return (
                <ListItem
                  key={`location-item-${index}`}
                  onClick={() => onClickLocation(location)}
                  sx={locationListItem}
                  className={clsx(
                    location?.is_highlight
                      ? "ServiceCard-locationListItem--highlight"
                      : ""
                  )}
                  secondaryAction={
                    path && (
                      <IconButton
                        edge="end"
                        aria-label="navigate"
                        className={clsx(
                          "ServiceCard-locationNavigateButton",
                          isRtl ? "is-rtl" : ""
                        )}
                        sx={locationNavigateButton}
                      >
                        {ChevronRightIcon}
                      </IconButton>
                    )
                  }
                >
                  {isReferenceID && (
                    <>
                      <Box
                        sx={venueLogoWrapper}
                        className="ServiceCard-venueLogoWrapper"
                      >
                        <CardMedia
                          src={venueLogo}
                          component="img"
                          sx={venueLogoStyle}
                        />
                      </Box>
                      <Divider orientation="vertical" sx={divider} />
                      {location.image && (
                        <Box sx={locationLogoWrapper}>
                          <CardMedia
                            src={location.image}
                            component="img"
                            sx={locationLogoStyle}
                          />
                        </Box>
                      )}
                      <Typography
                        sx={listNameText}
                        className="ServiceCard-listNameText"
                      >
                        {t(locationName)}
                      </Typography>
                      <ListItemAvatar
                        sx={floorTextAvatar}
                        className="ServiceCard-floorTextAvatar"
                      >
                        {t(level)}
                      </ListItemAvatar>
                    </>
                  )}
                  {!isReferenceID && (
                    <>
                      {location.image && (
                        <>
                          <Box
                            sx={venueLogoWrapper}
                            className="ServiceCard-venueLogoWrapper"
                          >
                            <CardMedia
                              src={location.image}
                              component="img"
                              sx={venueLogoStyle}
                            />
                          </Box>
                          <Divider orientation="vertical" sx={divider} />
                        </>
                      )}
                      <Typography sx={listNameText}>
                        {t(location.label)}
                      </Typography>
                    </>
                  )}
                </ListItem>
              )
            })}
          </List>
        </AccordionDetails>
      )}
    </Accordion>
  )
}
